import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import useSEO from "@/lib/useSEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { CORE_SERVICES, DEVELOPING_SERVICES } from "@/data/site";

const Service = ({ s, dark = false }) => (
  <div id={s.id} className="scroll-mt-28 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-10 py-10 lg:py-14 border-t items-start" style={{ borderColor: dark ? "rgba(247,244,238,0.16)" : "#DCD6C9" }} data-testid={`service-${s.id}`}>
    <div className="lg:col-span-1">
      <span className="font-serif text-brass text-3xl lg:text-4xl leading-none">{s.index}</span>
    </div>
    <div className="lg:col-span-4">
      <h2 className="font-serif text-2xl lg:text-3xl leading-tight" style={{ color: dark ? "#F7F4EE" : "#17263D" }}>{s.title}</h2>
    </div>
    <div className="lg:col-span-7">
      <p className="text-base lg:text-lg font-light leading-relaxed" style={{ color: dark ? "rgba(247,244,238,0.82)" : "rgba(37,37,37,0.85)" }}>{s.lead}</p>
      {s.points && (
        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
          {s.points.map((p) => (
            <li key={p} className="flex items-start gap-3">
              <Check size={16} strokeWidth={1.5} className="text-brass mt-1 shrink-0" />
              <span className="text-[0.95rem] leading-relaxed" style={{ color: dark ? "rgba(247,244,238,0.75)" : "rgba(37,37,37,0.8)" }}>{p}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

const Services = () => {
  const { hash } = useLocation();
  useSEO({
    title: "Services",
    description:
      "Audit & assurance, taxation and GST, accounting and financial reporting, and business advisory from Arun Gupta & Co., with developing outsourced and strategic finance capabilities.",
    path: "/services",
  });

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    }
  }, [hash]);

  return (
    <>
      <PageHero
        eyebrow="Services"
        titleLines={["Professional expertise", "across the financial", "lifecycle."]}
        intro="Established, core services delivered with professional discipline — alongside a smaller set of developing capabilities the firm is building deliberately."
        testId="services-hero"
      />

      {/* Core */}
      <section className="py-16 lg:py-24" data-testid="services-core">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="overline">Established / Core Services</span>
            </div>
          </Reveal>
          {CORE_SERVICES.map((s) => (
            <Reveal key={s.id}><Service s={s} /></Reveal>
          ))}
          <div className="border-t border-border-subtle" />
        </div>
      </section>

      {/* Developing */}
      <section className="py-14 lg:py-20" style={{ background: "#17263D" }} data-testid="services-developing">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="overline">Developing / Evolving Capabilities</span>
            </div>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ivory/70">
              These areas are being developed thoughtfully as extensions of the firm's established
              practice. They are offered where the firm can genuinely add value — not presented as a
              large, mature division.
            </p>
          </Reveal>
          {DEVELOPING_SERVICES.map((s) => (
            <Reveal key={s.id}><Service s={s} dark /></Reveal>
          ))}
          <div className="border-t" style={{ borderColor: "rgba(247,244,238,0.16)" }} />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 border-t border-border-subtle" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Reveal>
              <h2 className="font-serif text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] max-w-2xl">
                Not sure which of these fits your requirement?
              </h2>
              <p className="mt-6 text-lg font-light text-charcoal/80 max-w-xl">
                A short conversation is usually the quickest way to understand how we may be able to
                assist.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Reveal delay={0.1}>
              <Link to="/contact" className="btn-solid" data-testid="services-cta-btn">
                Get in Touch
                <ArrowRight size={15} strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;
